import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import AccionesPregunta from "./Botones/AccionesPregunta";
import {
  createSlug,
  fetchWithAuth,
  fetchWithAuthUpload,
} from "../../Context/UserContext";
import companyData from "../../Data/CompanyData";
import categories from "../../Data/Categories";
import Contacto from "../../Contacto/Contacto";
import API_URL from "../../config";
import EliminarActivar from "./Botones/EliminarActivar"; // Importa el nuevo modal
import { LoadingOverlay } from "../../components/ui/loading-overlay";
import { Share2, FileText, Star } from "lucide-react";
import { Button } from "../../components/ui/button";
import { cn } from "../../lib/utils";
import { toast } from "sonner";

const estadoLabel = (estado) => {
  if (estado === "licitacion-ganada") return "Cerrado";
  if (estado === "pendiente_pago") return "En revisión (pago pendiente)";
  return "Abierto";
};

const formatDate = (dateString) => {
  try {
    const date = new Date(dateString); // Convierte la cadena en un objeto Date
    return format(date, "PPPP p", { locale: es }); // Formatea la fecha
  } catch {
    return "Fecha inválida"; // Maneja errores si el formato no es válido
  }
};

const InfoLicitacionesPropias = ({
  tenders = [],
  page,
  totalPages,
  setPage,
}) => {
  const [localTenders, setLocalTenders] = useState(tenders);
  const [selectedTender, setSelectedTender] = useState(null);
  const [showDetails, setShowDetails] = useState(false); // Controla si se muestran los detalles (mobile)
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768); // Detecta si es mobile
  const navigate = useNavigate();
  const [showShareModal, setShowShareModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false); // Control del modal
  const [loading, setLoading] = useState(false);

  const categoryIcons = [
    ...categories.filter((cat) => cat.label !== "Otro Servicio"),
    ...companyData.sectores,
  ].reduce((acc, item) => {
    acc[item.label] = item.icon;
    return acc;
  }, {});

  // Manejar cambio de tamaño de pantalla
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768 && tenders.length > 0) {
        setSelectedTender(tenders[0]); // En desktop selecciona automáticamente
        setShowDetails(false); // En desktop, no se usan los detalles como vista aparte
      }
    };

    handleResize(); // Verifica el tamaño inicial
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, [tenders]);

  useEffect(() => {
    setLocalTenders(tenders);
  }, [tenders]);

  useEffect(() => {
    if (tenders.length === 0) {
      // Si no hay datos, limpia selectedTender
      setSelectedTender(null);
    } else if (!isMobile) {
      // Si no es mobile y hay datos, selecciona automáticamente el primer tender
      setSelectedTender(tenders[0]);
    } else {
      // Si es mobile, no selecciona automáticamente ningún tender
      setSelectedTender(null);
    }
  }, [tenders, isMobile]);

  const handleSelectTender = (tender) => {
    setSelectedTender(tender);
    if (isMobile) setShowDetails(true); // Muestra los detalles en mobile
  };

  const handleBackToMenu = () => {
    if (isMobile) setShowDetails(false); // Regresa al menú en mobile
  };

  const handleOpenDeleteModal = () => setShowDeleteModal(true);
  const handleCloseDeleteModal = () => setShowDeleteModal(false);

  const handleEdit = (id) => {
    if (selectedTender.applications_count > 0) {
      toast.warning(
        "No se puede editar la licitación una vez que ha empezado a recibir propuestas."
      );
    } else {
      navigate(`/editar-licitacion/${id}`);
    }
  };

  const handleViewApplications = (id) => {
    navigate(`/ver-postulaciones/${id}`);
  };

  const handleReuse = (id) => {
    navigate(`/reusar-licitacion/${id}`);
  };

  const handleNextPage = () => {
    if (page < totalPages) setPage(page + 1);
  };

  const handlePreviousPage = () => {
    if (page > 1) setPage(page - 1);
  };

  // Generar lista de páginas para mostrar: 1, actual, siguiente
  const renderPaginationButtons = () => {
    const pages = [];

    if (page > 1) pages.push(page - 1);
    pages.push(page);
    if (page < totalPages) pages.push(page + 1);

    return pages;
  };

  const handleReplySuccess = (updatedPregunta) => {
    setLocalTenders((prevTenders) =>
      prevTenders.map((tender) => {
        if (tender.id_tender !== selectedTender.id_tender) return tender;
        const updatedQuestions = tender.questions.map((q) =>
          q.id_qa === updatedPregunta.id_qa ? { ...q, ...updatedPregunta } : q
        );
        const updatedTender = { ...tender, questions: updatedQuestions };
        setSelectedTender(updatedTender); // Actualizar también selectedTender
        return updatedTender;
      })
    );
  };

  const handleDeleteQuestion = async (id_qa) => {
    setLoading(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/licit/delete_question/${id_qa}`,
        {
          method: "DELETE",
        }
      );

      if (response.ok) {
        setLocalTenders((prevTenders) =>
          prevTenders.map((tender) => {
            if (tender.id_tender !== selectedTender.id_tender) return tender;

            const updatedQuestions = tender.questions.filter(
              (q) => q.id_qa !== id_qa
            );
            return { ...tender, questions: updatedQuestions };
          })
        );

        // También actualizamos `selectedTender`
        setSelectedTender((prevTender) => ({
          ...prevTender,
          questions: prevTender.questions.filter((q) => q.id_qa !== id_qa),
        }));

        toast.success("Pregunta eliminada correctamente.");
      } else {
        toast.error("Error al eliminar la pregunta.");
      }
    } catch (error) {
      console.error("Error al eliminar la pregunta:", error.message);
      toast.error("Ocurrió un error al eliminar la pregunta.");
    } finally {
      setLoading(false);
    }
  };

  const downloadFile = async (fileId) => {
    setLoading(true);
    try {
      const response = await fetchWithAuthUpload(
        `${API_URL}/upload/download-file/${fileId}`,
        {
          method: "GET",
        }
      );

      if (!response.ok) {
        throw new Error("Error al obtener el archivo");
      }

      const data = await response.json();
      window.open(data.file_url, "_blank");
    } catch (error) {
      console.error("Error al descargar archivo:", error);
    } finally {
      setLoading(false);
    }
  };

  // Bloque de info principal, compartido entre mobile/desktop.
  // `createdField` preserva una inconsistencia real del original: mobile usa
  // created_at y desktop usa updated_at para "Creado".
  const renderHeaderInfo = (createdField) => (
    <>
      <h3 className="text-xl font-semibold text-foreground">
        <Link
          to={`/ver-licitacion/${selectedTender.id_tender}/${createSlug(selectedTender.titulo)}`}
          className="hover:text-primary"
        >
          {selectedTender.titulo}
        </Link>
      </h3>
      <p className="text-sm text-muted-foreground">
        <strong className="text-foreground">Nº de Licitación:</strong> {selectedTender.id_tender}
      </p>
      <p className="text-sm text-muted-foreground">
        <strong className="text-foreground">Creado:</strong> {formatDate(selectedTender[createdField])}
      </p>
      <p className="text-sm text-muted-foreground">
        <strong className="text-foreground">Fecha máx de postulación:</strong>{" "}
        {formatDate(selectedTender.fecha_maxima_postulacion)}
      </p>
      <p className="text-sm text-muted-foreground">
        <strong className="text-foreground">Categoría:</strong> {selectedTender.categoria}
      </p>
      <p className="text-sm text-muted-foreground">
        <strong className="text-foreground">Cantidad de postulantes:</strong>{" "}
        {selectedTender.applications_count}
      </p>
      <p className="text-sm text-muted-foreground">
        <strong className="text-foreground">Estado:</strong>{" "}
        {estadoLabel(selectedTender.estado)}
        {selectedTender.estado === "licitacion-ganada" && selectedTender.ganador && (
          <>
            {" "}
            | <strong className="text-foreground">Ganador:</strong> {selectedTender.ganador}
          </>
        )}
      </p>
    </>
  );

  // Cuerpo del detalle, compartido entre mobile/desktop.
  const renderDetailBody = () => (
    <>
      <div>
        <h3 className="font-semibold text-foreground">Detalles de la licitación:</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          <strong className="text-foreground">Descripción:</strong>{" "}
          <span
            dangerouslySetInnerHTML={{
              __html: (selectedTender.descripcion || "").replace(/\n/g, "<br>"),
            }}
          />
        </p>
      </div>

      {selectedTender.files?.length > 0 && (
        <div>
          <strong className="text-sm text-foreground">Archivos adjuntos:</strong>
          <ul className="mt-1 space-y-1">
            {selectedTender.files.map((file, index) => (
              <li key={index}>
                <button
                  onClick={() => downloadFile(file.id_file)}
                  className="flex items-center gap-1.5 text-sm text-primary hover:underline"
                >
                  <FileText className="h-4 w-4" /> {file.file_name || `Archivo ${index + 1}`}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div>
        <h3 className="font-semibold text-foreground">Información extra:</h3>
        <ul className="mt-1 space-y-1 text-sm text-muted-foreground">
          <li>
            <strong className="text-foreground">Condiciones de pago:</strong>{" "}
            {Array.isArray(selectedTender.condiciones_pago)
              ? selectedTender.condiciones_pago.join(", ")
              : "No especificado"}
          </li>
          <li>
            <strong className="text-foreground">Ciudades del servicio:</strong>{" "}
            {Array.isArray(selectedTender.ciudad) ? selectedTender.ciudad.join(", ") : "No especificado"}
          </li>
          <li>
            <strong className="text-foreground">Duración del contrato:</strong>{" "}
            {Array.isArray(selectedTender.duracion_contrato)
              ? selectedTender.duracion_contrato.join(", ")
              : "No especificado"}
          </li>
        </ul>
      </div>

      <div>
        <h3 className="font-semibold text-foreground">Preguntas y respuestas:</h3>
        {selectedTender?.questions?.length > 0 ? (
          <div className="mt-2 space-y-4">
            {selectedTender.questions.map((preguntaObj) => (
              <div key={preguntaObj.id_qa} className="rounded-md border border-border p-3 text-sm">
                <p>
                  <strong className="text-foreground">Usuario:</strong>{" "}
                  <Link
                    to={`/profile/${preguntaObj.id_user}/${preguntaObj.nombre_usuario}-${preguntaObj.apellido_usuario}`}
                    className="text-primary hover:underline"
                  >
                    {preguntaObj.nombre_usuario || "Desconocido"} {preguntaObj.apellido_usuario || ""}
                  </Link>
                </p>
                <p className="mt-1">
                  <strong className="text-foreground">Pregunta:</strong>{" "}
                  {preguntaObj.pregunta || "Pregunta no disponible"}
                </p>
                <p className="mt-1">
                  <strong className="text-foreground">Respuesta:</strong> {preguntaObj.respuesta || "-"}
                </p>
                <AccionesPregunta
                  pregunta={preguntaObj}
                  onDelete={handleDeleteQuestion}
                  onReplySuccess={handleReplySuccess}
                />
              </div>
            ))}
          </div>
        ) : (
          <p className="mt-1 text-sm text-muted-foreground">No hay preguntas asociadas a esta licitación.</p>
        )}
      </div>
    </>
  );

  const isCerrado = (estado) =>
    estado === "Activo" || estado === "licitacion-ganada" || estado === "pendiente_pago";

  return (
    <div className="flex flex-col gap-6 lg:flex-row">
      {loading && <LoadingOverlay />}
      {/* Menú izquierdo */}
      {!showDetails && (
        <div className="w-full lg:w-80 lg:shrink-0">
          <h2 className="mb-3 text-lg font-semibold text-foreground">Licitaciones propias</h2>
          {tenders.length > 0 ? (
            <>
              <div className="flex flex-col gap-2">
                {tenders.map((tender) => (
                  <button
                    key={tender.id_tender}
                    onClick={() => handleSelectTender(tender)}
                    className={cn(
                      "flex items-start gap-3 rounded-lg border border-border p-3 text-left hover:bg-muted",
                      selectedTender?.id_tender === tender.id_tender && "border-primary bg-primary/5"
                    )}
                  >
                    <span className="text-xl">{categoryIcons[tender.categoria] || "❓"}</span>
                    <div className="min-w-0 flex-1">
                      <h4 className="text-sm font-semibold text-foreground">
                        {tender.titulo.length > 40 ? `${tender.titulo.slice(0, 40)}...` : tender.titulo}
                      </h4>
                      <p className="truncate text-xs text-muted-foreground">
                        {tender.company_name
                          ? tender.company_name.length > 40
                            ? `${tender.company_name.slice(0, 40)}...`
                            : tender.company_name
                          : `${tender.user_name} ${tender.user_lastname}`.length > 40
                          ? `${`${tender.user_name} ${tender.user_lastname}`.slice(0, 40)}...`
                          : `${tender.user_name} ${tender.user_lastname}`}
                      </p>
                      <p className="text-xs text-muted-foreground">Nº Licitación: {tender.id_tender}</p>
                      <p className="text-xs font-medium text-primary">{tender.categoria}</p>
                      {tender.id_user !== parseInt(localStorage.getItem("current_user_id")) && (
                        <p className="text-xs text-muted-foreground">
                          Creado por {tender.user_name} {tender.user_lastname}
                        </p>
                      )}
                    </div>
                  </button>
                ))}
              </div>

              <div className="mt-4 flex items-center justify-center gap-2">
                <Button variant="outline" size="sm" onClick={handlePreviousPage} disabled={page === 1}>
                  Atrás
                </Button>
                {renderPaginationButtons().map((p, index) =>
                  p === "..." ? (
                    <span key={index} className="text-muted-foreground">
                      ...
                    </span>
                  ) : (
                    <Button
                      key={p}
                      variant={p === page ? "default" : "outline"}
                      size="sm"
                      onClick={() => setPage(p)}
                    >
                      {p}
                    </Button>
                  )
                )}
                <Button variant="outline" size="sm" onClick={handleNextPage} disabled={page === totalPages}>
                  Siguiente
                </Button>
              </div>
            </>
          ) : (
            <p className="text-muted-foreground">No hay licitaciones. Limpia los filtros para ver más.</p>
          )}
        </div>
      )}

      {/* Detalle a la derecha */}
      {selectedTender && showDetails && isMobile ? (
        <div className="flex-1 space-y-6">
          <h2 className="text-lg font-semibold text-foreground">Licitaciones propias</h2>

          <div className="space-y-2 border-b border-border pb-4">
            {renderHeaderInfo("created_at")}

            <div className="flex flex-wrap gap-2 pt-2">
              {selectedTender.winning_application_id && (
                <Button
                  variant="outline"
                  onClick={() => navigate(`/admin-contrato/${selectedTender.winning_application_id}`)}
                >
                  <Star className="h-4 w-4" /> Ganador
                </Button>
              )}
              <Button variant={isCerrado(selectedTender.estado) ? "destructive" : "default"} onClick={handleOpenDeleteModal}>
                {isCerrado(selectedTender.estado) ? "Eliminar" : "Reactivar"}
              </Button>
              <Button variant="ghost" onClick={() => setShowShareModal(true)}>
                <Share2 className="h-4 w-4" /> Compartir
              </Button>
            </div>
          </div>

          {renderDetailBody()}

          <div className="flex flex-wrap gap-2 border-t border-border pt-4">
            {selectedTender.winning_application_id && (
              <Button
                variant="outline"
                onClick={() => navigate(`/admin-contrato/${selectedTender.winning_application_id}`)}
              >
                <Star className="h-4 w-4" /> Ganador
              </Button>
            )}
            <Button variant="outline" onClick={() => handleViewApplications(selectedTender.id_tender)}>
              Postulantes
            </Button>
            <Button variant="outline" onClick={() => handleReuse(selectedTender.id_tender)}>
              Reusar
            </Button>
            {selectedTender.estado === "Activo" && (
              <Button variant="outline" onClick={() => handleEdit(selectedTender.id_tender)}>
                Editar
              </Button>
            )}
            {selectedTender.estado === "pendiente_pago" && (
              <Button
                variant="outline"
                onClick={() => navigate(`/licitaciones/${selectedTender.id_tender}/comprobante-pago`)}
              >
                Subir comprobante
              </Button>
            )}
            <Button variant="ghost" onClick={handleBackToMenu}>
              ⬅ Volver
            </Button>
          </div>
        </div>
      ) : !isMobile && !selectedTender ? (
        <div className="flex-1 rounded-lg border border-dashed border-border p-10 text-center text-muted-foreground">
          Sin resultados. Limpia los filtros.
        </div>
      ) : (
        !isMobile &&
        selectedTender && (
          <div className="flex-1 space-y-6">
            <h2 className="text-lg font-semibold text-foreground">Licitaciones propias</h2>

            <div className="space-y-2 border-b border-border pb-4">
              {renderHeaderInfo("updated_at")}
              {selectedTender.estado === "licitacion-ganada" && selectedTender.ganador && (
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">Ganador:</strong>{" "}
                  <a
                    href={`https://libra-1t4e.onrender.com/profile/${selectedTender.ganador}/${selectedTender.ganador}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    {selectedTender.ganador}
                  </a>
                </p>
              )}

              <div className="flex flex-wrap items-center gap-2 pt-2">
                {selectedTender.winning_application_id && (
                  <Button
                    variant="outline"
                    onClick={() => navigate(`/admin-contrato/${selectedTender.winning_application_id}`)}
                  >
                    <Star className="h-4 w-4" /> Ganador
                  </Button>
                )}
                <Button variant="outline" onClick={() => handleViewApplications(selectedTender.id_tender)}>
                  Ver Postulaciones
                </Button>
                <Button variant="outline" onClick={() => handleReuse(selectedTender.id_tender)}>
                  Reusar Licitación
                </Button>
                {selectedTender.estado === "Activo" && (
                  <Button variant="outline" onClick={() => handleEdit(selectedTender.id_tender)}>
                    Editar
                  </Button>
                )}
                {selectedTender.estado === "pendiente_pago" && (
                  <Button
                    variant="outline"
                    onClick={() => navigate(`/licitaciones/${selectedTender.id_tender}/comprobante-pago`)}
                  >
                    Subir comprobante
                  </Button>
                )}
                <Button
                  variant={isCerrado(selectedTender.estado) ? "destructive" : "default"}
                  onClick={handleOpenDeleteModal}
                >
                  {isCerrado(selectedTender.estado) ? "Eliminar" : "Reactivar"}
                </Button>
                <Button variant="ghost" onClick={() => setShowShareModal(true)}>
                  <Share2 className="h-4 w-4" /> Compartir
                </Button>
              </div>
            </div>

            {renderDetailBody()}
          </div>
        )
      )}

      <Contacto
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        id={selectedTender?.id_tender}
        titulo={selectedTender?.titulo}
      />
      {showDeleteModal && (
        <EliminarActivar
          isOpen={showDeleteModal}
          onClose={handleCloseDeleteModal}
          idTender={selectedTender.id_tender}
          estado={selectedTender.estado}
          setSelectedTender={setSelectedTender}
        />
      )}
    </div>
  );
};

export default InfoLicitacionesPropias;
